import { useEffect, useRef, useState } from "react";

/**
 * Wrap any content to reveal it with a fade-up animation when it enters the viewport.
 * Usage:
 *   <Reveal><h2>Big title</h2></Reveal>
 *   <Reveal delay={100} y={20}> ... </Reveal>
 *
 * Props:
 *   - as: HTML tag or component (default "div")
 *   - delay: ms delay (default 0)
 *   - y: initial translateY in px (default 24)
 *   - duration: ms (default 700)
 *   - once: only trigger once (default true)
 *   - threshold: IntersectionObserver threshold (default 0.15)
 */
export default function Reveal({
  as: Tag = "div",
  children,
  className = "",
  delay = 0,
  y = 24,
  duration = 700,
  once = true,
  threshold = 0.15,
  ...rest
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect reduced motion
    const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -60px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once, threshold]);

  const style = {
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : `translateY(${y}px)`,
    transition: `opacity ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
    willChange: "opacity, transform",
  };

  return (
    <Tag ref={ref} className={className} style={style} {...rest}>
      {children}
    </Tag>
  );
}

/** Stagger helper: wraps children individually so each reveals in sequence. */
export function RevealStagger({ children, initialDelay = 0, step = 90, ...revealProps }) {
  const items = Array.isArray(children) ? children : [children];
  return (
    <>
      {items.map((child, i) => (
        <Reveal key={i} delay={initialDelay + i * step} {...revealProps}>
          {child}
        </Reveal>
      ))}
    </>
  );
}
